import React, { useState, useEffect } from 'react';
import {
  Users, Plus, Edit3, Trash2, Shield, Mail, Calendar, Loader, UserCheck, UserX,
  Save, X, Copy, Search, Key, MoreHorizontal, ChevronDown, AtSign, Clock,
  CheckCircle2, AlertCircle,
} from 'lucide-react';
import { teamService, TeamMember, CreateTeamMemberRequest, UpdateTeamMemberRequest } from '../services/teamService';
import { useLocalization } from '../contexts/LocalizationContext';

const TeamManagement: React.FC = () => {
  const { t } = useLocalization();
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [resettingPassword, setResettingPassword] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [stats, setStats] = useState({
    total_members: 0,
    active_members: 0,
    pending_members: 0,
    roles_stats: { admin: 0, finance: 0, support: 0 }
  });
  const [loading, setLoading] = useState(true);
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [editingMember, setEditingMember] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 0 | 1 | 2>('all');
  const [openMenuFor, setOpenMenuFor] = useState<string | null>(null);
  const [newMember, setNewMember] = useState({
    firstname: '',
    lastname: '',
    email: '',
    niveau: 2,
    password: ''
  });
  const [editForm, setEditForm] = useState<UpdateTeamMemberRequest>({});

  useEffect(() => {
    loadTeamData();
  }, []);

  useEffect(() => {
    if (!openMenuFor) return;
    const handler = () => setOpenMenuFor(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [openMenuFor]);

  const loadTeamData = async () => {
    try {
      setLoading(true);
      const [members, teamStats, userData] = await Promise.all([
        teamService.getTeamMembers(),
        teamService.getTeamStats(),
        getCurrentUserData()
      ]);

      setTeamMembers(members);
      setStats(teamStats);
      setCurrentUser(userData);
    } catch (error) {
      console.error('Error loading team data:', error);
      alert(t('team.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const getCurrentUserData = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        return await response.json();
      } catch (error) {
        console.error('Error fetching current user:', error);
      }
    }
    return null;
  };

  const getRoleLabel = (niveau: number) => {
    switch (niveau) {
      case 0: return t('role.merchantAdmin');
      case 1: return t('team.financeTeam');
      case 2: return t('team.supportTeam');
      case 3: return t('team.integrationTeam') || (t('role.integration') || 'Integration');
      default: return 'User';
    }
  };

  // Soft pill colors, Metronic-style minimal.
  const getRolePillClass = (niveau: number) => {
    switch (niveau) {
      case 0: return 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200';
      case 1: return 'bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-200';
      case 2: return 'bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-200';
      case 3: return 'bg-purple-50 text-purple-700 ring-1 ring-inset ring-purple-200';
      default: return 'bg-gray-50 text-gray-700 ring-1 ring-inset ring-gray-200';
    }
  };

  const getRoleDotClass = (niveau: number) => {
    switch (niveau) {
      case 0: return 'bg-emerald-500';
      case 1: return 'bg-indigo-500';
      case 2: return 'bg-sky-500';
      case 3: return 'bg-purple-500';
      default: return 'bg-gray-400';
    }
  };

  // Avatar uses a subtle gradient anchored to the role color.
  const getAvatarClass = (niveau: number) => {
    switch (niveau) {
      case 0: return 'bg-gradient-to-br from-emerald-500 to-emerald-700';
      case 1: return 'bg-gradient-to-br from-indigo-500 to-indigo-700';
      case 2: return 'bg-gradient-to-br from-sky-500 to-sky-700';
      case 3: return 'bg-gradient-to-br from-purple-500 to-purple-700';
      default: return 'bg-gradient-to-br from-gray-500 to-gray-700';
    }
  };

  const getStatusInfo = (status: string, userStatus: string) => {
    if (status === 'Inactive') return { label: t('status.inactive'), pill: 'bg-rose-50 text-rose-700 ring-rose-200', dot: 'bg-rose-500' };
    if (userStatus === 'pending') return { label: t('team.pending'), pill: 'bg-amber-50 text-amber-700 ring-amber-200', dot: 'bg-amber-500' };
    if (userStatus === 'online') return { label: t('status.online'), pill: 'bg-emerald-50 text-emerald-700 ring-emerald-200', dot: 'bg-emerald-500' };
    if (userStatus === 'offline') return { label: t('status.offline'), pill: 'bg-gray-50 text-gray-600 ring-gray-200', dot: 'bg-gray-400' };
    return { label: t('status.active'), pill: 'bg-emerald-50 text-emerald-700 ring-emerald-200', dot: 'bg-emerald-500' };
  };

  const handleResetPassword = async (member: TeamMember) => {
    try {
      const randomPassword = teamService.generateRandomPassword();
      await teamService.resetUserPassword(member.id, randomPassword);
      setNewPassword(randomPassword);
      setResettingPassword(member.id);
      setShowPasswordModal(true);
    } catch (error: any) {
      console.error('Error resetting password:', error);
      alert(error.response?.data?.detail || t('team.errorResetPassword'));
    }
  };

  const handleToggleStatus = async (member: TeamMember) => {
    const newStatus = member.status === 'Active' ? 'Inactive' : 'Active';
    if (confirm(`${member.status === 'Active' ? t('team.confirmDeactivate') : t('team.confirmActivate')} ${member.firstname} ${member.lastname}?`)) {
      try {
        await teamService.toggleUserStatus(member.id, newStatus);
        await loadTeamData();
      } catch (error: any) {
        console.error('Error toggling status:', error);
        alert(error.response?.data?.detail || t('team.errorLoading'));
      }
    }
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setResettingPassword(null);
    setNewPassword('');
  };

  const copyPasswordToClipboard = () => {
    navigator.clipboard.writeText(newPassword);
  };

  const generateUsername = (firstname: string, lastname: string) => {
    const baseUsername = `${firstname.toLowerCase().replace(/\s+/g, '')}.${lastname.toLowerCase().replace(/\s+/g, '')}`;
    let username = baseUsername;
    let counter = 1;
    const usernameExists = (usernameToCheck: string) => {
      return teamMembers.some(member => member.username === usernameToCheck);
    };
    while (usernameExists(username)) {
      username = `${baseUsername}${counter}`;
      counter++;
    }
    return username;
  };

  const handleAddMember = async () => {
    if (!newMember.firstname || !newMember.lastname || !newMember.email || !newMember.password) {
      alert(t('team.fillRequiredFields'));
      return;
    }
    try {
      const generatedUsername = generateUsername(newMember.firstname, newMember.lastname);
      const memberData: CreateTeamMemberRequest = {
        firstname: newMember.firstname,
        lastname: newMember.lastname,
        username: generatedUsername,
        email: newMember.email,
        password: newMember.password,
        merchant_code: currentUser?.merchant_code || '',
        niveau: newMember.niveau
      };
      await teamService.createTeamMember(memberData);
      await loadTeamData();
      setNewMember({ firstname: '', lastname: '', email: '', niveau: 2, password: '' });
      setIsAddingMember(false);
    } catch (error: any) {
      console.error('Error adding member:', error);
      alert(error.response?.data?.detail || t('team.errorAdding'));
    }
  };

  const startEditMember = (member: TeamMember) => {
    setEditingMember(member.id);
    setOpenMenuFor(null);
    setEditForm({
      firstname: member.firstname,
      lastname: member.lastname,
      email: member.email,
      niveau: member.niveau,
      status: member.status
    });
  };

  const handleUpdateMember = async (id: string) => {
    try {
      await teamService.updateTeamMember(id, editForm);
      await loadTeamData();
      setEditingMember(null);
      setEditForm({});
    } catch (error: any) {
      console.error('Error updating member:', error);
      alert(error.response?.data?.detail || t('team.errorUpdating'));
    }
  };

  const cancelEdit = () => {
    setEditingMember(null);
    setEditForm({});
  };

  const handleDeleteMember = async (id: string, name: string) => {
    if (confirm(`${t('team.confirmDelete')} ${name}?`)) {
      try {
        await teamService.deleteTeamMember(id);
        await loadTeamData();
      } catch (error: any) {
        console.error('Error deleting member:', error);
        alert(error.response?.data?.detail || t('team.errorDeleting'));
      }
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return t('team.never');
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const filteredMembers = teamMembers.filter(member => {
    if (roleFilter !== 'all' && member.niveau !== roleFilter) return false;
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      member.firstname.toLowerCase().includes(search) ||
      member.lastname.toLowerCase().includes(search) ||
      member.email.toLowerCase().includes(search) ||
      member.username.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin text-emerald-600 mx-auto" />
          <p className="text-sm text-gray-500 mt-3">{t('team.title')}...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-5">

      {/* Hero */}
      <div
        className="relative overflow-hidden rounded-2xl p-7 text-white shadow-xl shadow-rose-500/30"
        style={{ background: "linear-gradient(135deg, #f43f5e 0%, #e11d48 55%, #be123c 100%)" }}
      >
        <div className="pointer-events-none absolute -right-16 -top-16 w-64 h-64 rounded-full blur-3xl bg-pink-300/35" />
        <div className="pointer-events-none absolute -left-10 bottom-0 w-48 h-48 rounded-full blur-3xl bg-fuchsia-300/25" />
        <div className="pointer-events-none absolute inset-0 opacity-[0.08]" style={{
          backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
          backgroundSize: "24px 24px",
        }} />
        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4 min-w-0">
            <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm ring-1 ring-white/30 flex items-center justify-center flex-shrink-0 shadow-lg shadow-black/10">
              <Users className="w-7 h-7 text-white" strokeWidth={2.2} />
            </div>
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-3 py-1 mb-2 ring-1 ring-white/20">
                <div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                <span className="text-[11px] font-semibold text-white/90 tracking-wide uppercase">Access management</span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{t('team.title')}</h1>
              <p className="text-sm text-white/85 mt-1.5 max-w-2xl leading-relaxed">{t('team.subtitle')}</p>
            </div>
          </div>
          <button
            onClick={() => setIsAddingMember((v) => !v)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white text-rose-700 text-sm font-semibold shadow-lg shadow-black/10 hover:shadow-xl hover:-translate-y-0.5 transition-all"
          >
            <Plus className="h-4 w-4" />
            {t('team.addMember')}
          </button>
        </div>
      </div>

      {/* KPI cards — colorful gradients */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label={t('team.totalMembers')}
          value={stats.total_members}
          icon={<Users className="h-6 w-6" strokeWidth={2.2} />}
          accent="emerald"
        />
        <KpiCard
          label={t('team.activeMembers')}
          value={stats.active_members}
          icon={<UserCheck className="h-6 w-6" strokeWidth={2.2} />}
          accent="sky"
        />
        <KpiCard
          label={t('team.pending')}
          value={stats.pending_members}
          icon={<Clock className="h-6 w-6" strokeWidth={2.2} />}
          accent="amber"
        />
        <KpiCard
          label={t('team.adminTeam')}
          value={stats.roles_stats.admin}
          icon={<Shield className="h-6 w-6" strokeWidth={2.2} />}
          accent="indigo"
        />
      </div>

      {/* Add Member panel — drawer-style, neutral */}
      {isAddingMember && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-200 bg-gray-50/60">
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-md bg-emerald-50 ring-1 ring-inset ring-emerald-200 flex items-center justify-center">
                <Plus className="h-4 w-4 text-emerald-700" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-gray-900">{t('team.addNewMember')}</h2>
                <p className="text-xs text-gray-500">{t('team.fillRequiredFields')}</p>
              </div>
            </div>
            <button
              onClick={() => setIsAddingMember(false)}
              className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="p-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label={`${t('profile.firstName')} *`}>
                <input
                  type="text"
                  value={newMember.firstname}
                  onChange={(e) => setNewMember({ ...newMember, firstname: e.target.value })}
                  className={inputClass}
                  placeholder={t('profile.firstName')}
                />
              </FormField>
              <FormField label={`${t('profile.lastName')} *`}>
                <input
                  type="text"
                  value={newMember.lastname}
                  onChange={(e) => setNewMember({ ...newMember, lastname: e.target.value })}
                  className={inputClass}
                  placeholder={t('profile.lastName')}
                />
              </FormField>
              <FormField label={`${t('profile.email')} *`}>
                <input
                  type="email"
                  value={newMember.email}
                  onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                  className={inputClass}
                  placeholder="email@example.com"
                />
              </FormField>
              <FormField label={t('team.role')}>
                <div className="relative">
                  <select
                    value={newMember.niveau}
                    onChange={(e) => setNewMember({ ...newMember, niveau: parseInt(e.target.value) })}
                    className={`${inputClass} appearance-none pr-9 bg-white`}
                  >
                    <option value={1}>{t('team.financeTeam')}</option>
                    <option value={2}>{t('team.supportTeam')}</option>
                    <option value={3}>{t('team.integrationTeam') || (t('role.integration') || 'Integration')}</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
              </FormField>
              <div className="md:col-span-2">
                <FormField label={`${t('team.tempPassword')} *`} hint={t('team.tempPasswordNote')}>
                  <input
                    type="password"
                    value={newMember.password}
                    onChange={(e) => setNewMember({ ...newMember, password: e.target.value })}
                    className={inputClass}
                    placeholder={t('team.tempPassword')}
                  />
                </FormField>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 mt-5 pt-4 border-t border-gray-100">
              <button
                onClick={() => setIsAddingMember(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleAddMember}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors text-sm font-medium shadow-sm"
              >
                <Plus className="h-4 w-4" />
                {t('team.addMember')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Team Members — table card */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        {/* Toolbar */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-5 py-3.5 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-gray-900">{t('team.teamMembers')}</h2>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600 ring-1 ring-inset ring-gray-200">
              {filteredMembers.length + (currentUser ? 1 : 0)}
            </span>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            {/* Role filter */}
            <div className="flex items-center gap-1 bg-gray-50 ring-1 ring-inset ring-gray-200 rounded-md p-0.5">
              {([
                { key: 'all',  label: t('common.all') || 'All' },
                { key: 0,      label: t('role.merchantAdmin') },
                { key: 1,      label: t('team.financeTeam') },
                { key: 2,      label: t('team.supportTeam') },
              ] as const).map(opt => (
                <button
                  key={String(opt.key)}
                  onClick={() => setRoleFilter(opt.key as any)}
                  className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                    roleFilter === opt.key
                      ? 'bg-white text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200'
                      : 'text-gray-500 hover:text-gray-800'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('common.search') + '...'}
                className="w-full sm:w-64 pl-8 pr-3 py-1.5 bg-white border border-gray-300 rounded-md text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 transition-all"
              />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50/60 border-b border-gray-200">
                <th className={thClass}>{t('team.member')}</th>
                <th className={thClass}>{t('team.username')}</th>
                <th className={thClass}>{t('team.role')}</th>
                <th className={thClass}>{t('common.status')}</th>
                <th className={thClass}>{t('team.lastLogin')}</th>
                <th className={thClass}>{t('team.joinedDate')}</th>
                <th className={`${thClass} text-right`}>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {/* Current User (Admin) */}
              {currentUser && (
                <tr className="bg-emerald-50/30 hover:bg-emerald-50/50 transition-colors">
                  <td className={tdClass}>
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <div className="w-9 h-9 bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-full flex items-center justify-center text-white font-semibold text-xs ring-2 ring-white shadow-sm">
                          {currentUser.firstname?.charAt(0)}{currentUser.lastname?.charAt(0)}
                        </div>
                        <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-white" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {currentUser.firstname} {currentUser.lastname}
                          </span>
                          <span className="text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-600 text-white">
                            {t('team.you')}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 mt-0.5 text-xs text-gray-500 truncate">
                          <Mail className="h-3 w-3 text-gray-400 shrink-0" />
                          <span className="truncate">{currentUser.email}</span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className={tdClass}>
                    <UsernameCell value={currentUser.username} />
                  </td>
                  <td className={tdClass}>
                    <RolePill niveau={0} label={t('role.merchantAdmin')} />
                  </td>
                  <td className={tdClass}>
                    <StatusPill {...getStatusInfo('Active', 'online')} />
                  </td>
                  <td className={tdClass}>
                    <span className="text-xs text-gray-600">{t('team.now')}</span>
                  </td>
                  <td className={tdClass}>
                    <span className="text-xs text-gray-600">{formatDate(currentUser.created_at)}</span>
                  </td>
                  <td className={`${tdClass} text-right`}>
                    <span className="text-xs text-gray-300">—</span>
                  </td>
                </tr>
              )}

              {filteredMembers.map((member) => {
                const status = getStatusInfo(member.status, member.user_status);
                const isEditing = editingMember === member.id;
                return (
                  <tr key={member.id} className="hover:bg-gray-50/60 transition-colors">
                    <td className={tdClass}>
                      <div className="flex items-center gap-3">
                        {isEditing ? (
                          <div className="flex-1 space-y-2">
                            <div className="grid grid-cols-2 gap-2">
                              <input
                                type="text"
                                value={editForm.firstname || ''}
                                onChange={(e) => setEditForm({ ...editForm, firstname: e.target.value })}
                                className={inputClass}
                                placeholder={t('profile.firstName')}
                              />
                              <input
                                type="text"
                                value={editForm.lastname || ''}
                                onChange={(e) => setEditForm({ ...editForm, lastname: e.target.value })}
                                className={inputClass}
                                placeholder={t('profile.lastName')}
                              />
                            </div>
                            <input
                              type="email"
                              value={editForm.email || ''}
                              onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                              className={inputClass}
                              placeholder={t('profile.email')}
                            />
                          </div>
                        ) : (
                          <>
                            <div className="relative">
                              <div className={`w-9 h-9 ${getAvatarClass(member.niveau)} rounded-full flex items-center justify-center text-white font-semibold text-xs ring-2 ring-white shadow-sm`}>
                                {member.firstname?.charAt(0)}{member.lastname?.charAt(0)}
                              </div>
                              <span className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ${status.dot} ring-2 ring-white`} />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {member.firstname} {member.lastname}
                              </p>
                              <div className="flex items-center gap-1 mt-0.5 text-xs text-gray-500 truncate">
                                <Mail className="h-3 w-3 text-gray-400 shrink-0" />
                                <span className="truncate">{member.email}</span>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </td>
                    <td className={tdClass}>
                      <UsernameCell value={member.username} />
                    </td>
                    <td className={tdClass}>
                      {isEditing ? (
                        <div className="relative">
                          <select
                            value={editForm.niveau ?? member.niveau}
                            onChange={(e) => setEditForm({ ...editForm, niveau: parseInt(e.target.value) })}
                            className={`${inputClass} appearance-none pr-8 bg-white`}
                          >
                            <option value={1}>{t('team.financeTeam')}</option>
                            <option value={2}>{t('team.supportTeam')}</option>
                            <option value={3}>{t('team.integrationTeam') || (t('role.integration') || 'Integration')}</option>
                          </select>
                          <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
                        </div>
                      ) : (
                        <RolePill niveau={member.niveau} label={getRoleLabel(member.niveau)} />
                      )}
                    </td>
                    <td className={tdClass}>
                      {isEditing ? (
                        <div className="relative">
                          <select
                            value={editForm.status || member.status}
                            onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                            className={`${inputClass} appearance-none pr-8 bg-white`}
                          >
                            <option value="Active">{t('status.active')}</option>
                            <option value="Inactive">{t('status.inactive')}</option>
                          </select>
                          <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
                        </div>
                      ) : (
                        <StatusPill {...status} />
                      )}
                    </td>
                    <td className={tdClass}>
                      <span className="text-xs text-gray-600">
                        {member.last_login ? formatDate(member.last_login) : t('team.never')}
                      </span>
                    </td>
                    <td className={tdClass}>
                      <span className="text-xs text-gray-600">{formatDate(member.created_at)}</span>
                    </td>
                    <td className={`${tdClass} text-right`}>
                      {isEditing ? (
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleUpdateMember(member.id)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-600 text-white text-xs font-medium rounded-md hover:bg-emerald-700 transition-colors"
                            title={t('common.save')}
                          >
                            <Save className="h-3.5 w-3.5" /> {t('common.save')}
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="p-1.5 text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                            title={t('common.cancel')}
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ) : (
                        <RowActions
                          member={member}
                          open={openMenuFor === member.id}
                          onToggle={(e) => {
                            e.stopPropagation();
                            setOpenMenuFor(openMenuFor === member.id ? null : member.id);
                          }}
                          onEdit={() => startEditMember(member)}
                          onReset={() => { setOpenMenuFor(null); handleResetPassword(member); }}
                          onToggleStatus={() => { setOpenMenuFor(null); handleToggleStatus(member); }}
                          onDelete={() => { setOpenMenuFor(null); handleDeleteMember(member.id, `${member.firstname} ${member.lastname}`); }}
                          t={t}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {filteredMembers.length === 0 && !currentUser && (
            <div className="text-center py-14 px-4">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
                <Users className="h-5 w-5 text-gray-400" />
              </div>
              <p className="text-sm font-medium text-gray-900">{t('team.noMembersFound')}</p>
              <p className="text-xs text-gray-500 mt-1">{t('team.startAddMember')}</p>
            </div>
          )}
          {filteredMembers.length === 0 && currentUser && (searchTerm || roleFilter !== 'all') && (
            <div className="text-center py-10 px-4 border-t border-gray-100">
              <p className="text-xs text-gray-500">{t('team.noMembersFound')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Role Permissions — sober grid */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-gray-200">
          <div className="h-8 w-8 rounded-md bg-gray-50 ring-1 ring-inset ring-gray-200 flex items-center justify-center">
            <Shield className="h-4 w-4 text-gray-700" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-900">{t('team.permissionsByRole')}</h2>
            <p className="text-xs text-gray-500">{t('team.subtitle')}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 divide-y xl:divide-y-0 xl:divide-x divide-gray-200">
          <PermissionPanel
            niveau={0}
            title={t('role.merchantAdmin')}
            items={[
              t('team.fullAccess'),
              t('team.teamManagement'),
              t('team.accountConfig'),
              t('team.allPermissions'),
            ]}
            dot={getRoleDotClass(0)}
          />
          <PermissionPanel
            niveau={1}
            title={t('team.financeTeam')}
            items={[
              t('team.viewTransactions'),
              t('team.checkBalances'),
              t('team.generateReports'),
              t('team.financialAnalytics'),
              t('team.manageTransfers'),
            ]}
            dot={getRoleDotClass(1)}
          />
          <PermissionPanel
            niveau={2}
            title={t('team.supportTeam')}
            items={[
              t('team.viewTransactions'),
              t('team.customerSupport'),
              t('team.checkHistory'),
              t('team.basicReporting'),
            ]}
            dot={getRoleDotClass(2)}
          />
          <PermissionPanel
            niveau={3}
            title={t('team.integrationTeam') || (t('role.integration') || 'Integration')}
            items={[
              t('team.manageApiKeys') || 'Access API keys & secret',
              t('team.regenerateSecret') || 'Regenerate merchant secret',
              t('team.viewEvents') || 'View API events & logs',
              t('team.readDocumentation') || 'Read integration documentation',
            ]}
            dot={getRoleDotClass(3)}
          />
        </div>
      </div>

      {/* Password Reset Modal */}
      {showPasswordModal && resettingPassword && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden border border-gray-200">
            <div className="px-5 py-3.5 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="h-8 w-8 rounded-md bg-amber-50 ring-1 ring-inset ring-amber-200 flex items-center justify-center">
                  <Key className="h-4 w-4 text-amber-700" />
                </div>
                <h2 className="text-sm font-semibold text-gray-900">{t('team.passwordReset')}</h2>
              </div>
              <button
                onClick={closePasswordModal}
                className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="p-5">
              <div className="flex items-start gap-2.5 mb-4 text-amber-800 bg-amber-50 ring-1 ring-inset ring-amber-200 rounded-md p-3">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs font-semibold">{t('team.newPasswordGenerated')}</p>
                  <p className="text-[11px] text-amber-700 mt-0.5">{t('team.passwordNote')}</p>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3 bg-gray-50 ring-1 ring-inset ring-gray-200 rounded-md p-3">
                <code className="text-sm font-mono text-gray-900 select-all font-semibold tracking-wide">
                  {newPassword}
                </code>
                <button
                  onClick={copyPasswordToClipboard}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  title="Copy"
                >
                  <Copy className="h-3.5 w-3.5" />
                  {t('team.copyPassword')}
                </button>
              </div>
            </div>

            <div className="px-5 py-3 border-t border-gray-200 bg-gray-50/60 flex justify-end gap-2">
              <button
                onClick={closePasswordModal}
                className="px-3.5 py-1.5 text-sm font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800 transition-colors"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

/* ─────────── Helpers & sub-components ─────────── */

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-900 placeholder-gray-400 ' +
  'focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 transition-all';

const thClass =
  'px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider';

const tdClass = 'px-5 py-3 align-middle';

function FormField({
  label, hint, children,
}: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1.5">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-gray-500 mt-1.5">{hint}</p>}
    </div>
  );
}

function KpiCard({
  label, value, icon, accent,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  accent: 'emerald' | 'amber' | 'indigo' | 'sky';
}) {
  const accents: Record<string, { gradient: string; glow: string; blob: string }> = {
    emerald: {
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 55%, #047857 100%)',
      glow: 'shadow-emerald-500/30 hover:shadow-emerald-500/50',
      blob: 'bg-emerald-300/30',
    },
    sky: {
      gradient: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 55%, #0369a1 100%)',
      glow: 'shadow-sky-500/30 hover:shadow-sky-500/50',
      blob: 'bg-cyan-300/30',
    },
    amber: {
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 55%, #b45309 100%)',
      glow: 'shadow-amber-500/30 hover:shadow-amber-500/50',
      blob: 'bg-orange-300/30',
    },
    indigo: {
      gradient: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 55%, #4338ca 100%)',
      glow: 'shadow-indigo-500/30 hover:shadow-indigo-500/50',
      blob: 'bg-violet-300/30',
    },
  };
  const a = accents[accent];
  return (
    <div
      className={`relative overflow-hidden rounded-2xl p-5 text-white shadow-lg ${a.glow} hover:-translate-y-0.5 transition-all duration-300`}
      style={{ background: a.gradient }}
    >
      <div className={`pointer-events-none absolute -right-8 -top-8 w-32 h-32 rounded-full blur-2xl ${a.blob}`} />
      <div className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
        backgroundSize: '18px 18px',
      }} />
      <div className="relative flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold text-white/85 uppercase tracking-[0.12em] truncate">{label}</p>
          <p className="text-3xl font-bold text-white mt-1 leading-none tracking-tight">{value}</p>
        </div>
        <div className="h-12 w-12 rounded-xl bg-white/20 backdrop-blur-sm ring-1 ring-white/30 flex items-center justify-center shrink-0 shadow-inner">
          {icon}
        </div>
      </div>
    </div>
  );
}

function UsernameCell({ value }: { value: string | null | undefined }) {
  if (!value) return <span className="text-xs text-gray-400">—</span>;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-100 text-gray-700 font-mono text-[11px] ring-1 ring-inset ring-gray-200">
      <AtSign className="h-3 w-3 text-gray-400" />
      {value}
    </span>
  );
}

function RolePill({ niveau, label }: { niveau: number; label: string }) {
  const pill: Record<number, string> = {
    0: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    1: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
    2: 'bg-sky-50 text-sky-700 ring-sky-200',
  };
  const dot: Record<number, string> = {
    0: 'bg-emerald-500',
    1: 'bg-indigo-500',
    2: 'bg-sky-500',
  };
  const p = pill[niveau] || 'bg-gray-50 text-gray-700 ring-gray-200';
  const d = dot[niveau] || 'bg-gray-400';
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-medium ring-1 ring-inset ${p}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${d}`} />
      {label}
    </span>
  );
}

function StatusPill({ label, pill, dot }: { label: string; pill: string; dot: string }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-medium ring-1 ring-inset ${pill}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      {label}
    </span>
  );
}

function PermissionPanel({
  title, items, dot,
}: {
  niveau: number;
  title: string;
  items: string[];
  dot: string;
}) {
  return (
    <div className="p-5">
      <div className="flex items-center gap-2 mb-3">
        <span className={`h-2 w-2 rounded-full ${dot}`} />
        <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-700">{title}</h4>
      </div>
      <ul className="space-y-2">
        {items.map((it) => (
          <li key={it} className="flex items-start gap-2 text-sm text-gray-600">
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5 shrink-0" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function RowActions({
  member, open, onToggle, onEdit, onReset, onToggleStatus, onDelete, t,
}: {
  member: TeamMember;
  open: boolean;
  onToggle: (e: React.MouseEvent) => void;
  onEdit: () => void;
  onReset: () => void;
  onToggleStatus: () => void;
  onDelete: () => void;
  t: (k: string) => string;
}) {
  return (
    <div className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={onToggle}
        className={`p-1.5 rounded-md transition-colors ${
          open ? 'bg-gray-100 text-gray-700' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'
        }`}
        title={t('common.actions')}
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-gray-200 rounded-md shadow-lg py-1 z-20">
          <MenuItem icon={<Edit3 className="h-3.5 w-3.5" />} label={t('common.edit')} onClick={onEdit} />
          <MenuItem icon={<Key className="h-3.5 w-3.5" />} label={t('team.resetPassword')} onClick={onReset} />
          <MenuItem
            icon={member.status === 'Active' ? <UserX className="h-3.5 w-3.5" /> : <UserCheck className="h-3.5 w-3.5" />}
            label={member.status === 'Active' ? t('team.deactivateUser') : t('team.activateUser')}
            onClick={onToggleStatus}
          />
          <div className="my-1 border-t border-gray-100" />
          <MenuItem
            icon={<Trash2 className="h-3.5 w-3.5" />}
            label={t('common.delete')}
            onClick={onDelete}
            danger
          />
        </div>
      )}
    </div>
  );
}

function MenuItem({
  icon, label, onClick, danger,
}: { icon: React.ReactNode; label: string; onClick: () => void; danger?: boolean }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left transition-colors ${
        danger ? 'text-rose-600 hover:bg-rose-50' : 'text-gray-700 hover:bg-gray-50'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

export default TeamManagement;
